import { useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQueryCache } from "react-query";
import { message } from "antd";

import * as ajaxEndpoints from "../../../../api/ajax-endpoints";
import ButtonLoader, { Loader } from "../../../../UI/Loaders/Loaders";
import useEachInvestorsInvestment from "../../../../custom-hooks/useEachInvestorsInvestment";
import useEachInvestorInvestmentSummary from "../../../../custom-hooks/useEachInvestorInvestmentSummary";
import { formatMoney } from "../../../../helpers/formatter";
import { attachClassNameToTag } from "../../../../helpers/attachClassNameToTag";
import {
  handleFundInvestmentModalToggle,
  handleInvestmentWithdrawalModalToggle,
  handleRollOverModalToggle,
  InvestmentStatus,
} from "./helper";
import { getCurrencyInfo } from "../../../FunderInvestmentProduct/helper";
import { ROLE_TYPES } from "../../../../helpers/roleTypes";
import useInvestmentClosure from "../../../../custom-hooks/useInvestmentClosure";
import CloseInvestmentModal from "../../../../NewComponents/CloseInvestmentModal";
import { ISelectedRecord } from "./EachInvestorInvestment";

import styles from "../InvestorsInvestment.module.scss";

interface IInvestment {
  principal_amount: number;
  tenure: number;
  interest_amount: number;
  interest_repayment_type: string;
  monthly_interest_repayment: number;
  reference_number: string;
  effective_date: string;
  maturity_date: string;
  investment_status: string;
  funder_id: number;
  deposit_type: string;
  id: number;
  currency: string;
  // Additional fields from API that ISelectedRecord needs
  internal_payment_reference?: string;
  is_roll_over?: boolean;
  cba_product_id?: number;
  investment_product_details_id?: number;
}

interface IInvestorInvestmentProps {
  id: string | number;
  data: any;
  investment_product_id: string | number;
  setSelectedRecord: React.Dispatch<
    React.SetStateAction<ISelectedRecord | undefined>
  >;
  selectedFund: string;
}

interface IRootState {
  authReducer: {
    loggedInUser: {
      roles: string[];
      first_name: string;
      last_name: string;
    } | null;
  };
}

const InvestorInvestment: React.FC<IInvestorInvestmentProps> = ({
  id,
  data,
  investment_product_id,
  setSelectedRecord,
  selectedFund: currency,
}) => {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const [investmentID, setInvestmentID] = useState<number | null>(null);
  const [maturityDate, setMaturityDate] = useState<string>("");

  const loggedInUser = useSelector(
    (state: IRootState) => state?.authReducer?.loggedInUser,
  );
  const roles = loggedInUser?.roles;
  const history = useHistory();
  const queryCache = useQueryCache();

  const {
    data: investmentsData,
    status: investmentsStatus,
    error,
  } = useEachInvestorsInvestment({
    id: Number(id),
    currency,
    investmentProductId: Number(investment_product_id),
  });

  const { data: investmentSummaryData, status: investmentSummaryStatus } =
    useEachInvestorInvestmentSummary({
      id: Number(id),
      currency,
      investmentProductId: Number(investment_product_id),
    });

  const { mutate: closeInvestment, isLoading: isLoadingInvestmentClosure } =
    useInvestmentClosure(
      ajaxEndpoints.CLOSE_WITHDRAW_INVESTMENT,
      (responseData: { message?: string }) => {
        message.success(
          responseData.message || "Investment closed successfully!",
        );
        setIsConfirmationModalOpen(false);
        queryCache.invalidateQueries([
          {
            id: Number(id),
            currency,
            investmentProductId: Number(investment_product_id),
          },
          "getEachInvestorsInvestment",
        ]);
      },
    );

  const handleInvestmentClosure = async (closeDate: string) => {
    await closeInvestment({
      investment_id: investmentID,
      close_date: closeDate,
    });
  };

  return (
    <div>
      {investmentsStatus === "loading" ? (
        <Loader centered={true} text="Loading ..." />
      ) : error ? (
        <div>
          <div className="grid__padding animated fadeInRight">
            <h4 className="center-txt py-5">
              <i className="fas fa-bell" /> Unable to Fetch Investor's
              Investment
            </h4>
          </div>
        </div>
      ) : investmentsData && investmentsData?.data ? (
        <div className="datatable-wrapper">
          <div className=" speed-1x animated fadeInRight">
            {/*Datatable || Loader || Error Message*/}
            <div className="row mt-4">
              <div className="col-md-4 d-flex flex-column">
                <div className={styles.rightBorder}>
                  <div className={styles.investmentTitle}>
                    Total Active Investments
                  </div>
                  <div className={styles.investmentSubTitle}>
                    {investmentSummaryStatus === "loading" ? (
                      <ButtonLoader />
                    ) : (
                      investmentSummaryData?.total_active_investment || "N/A"
                    )}
                  </div>
                </div>
              </div>
              <div className="col-md-4 d-flex flex-column">
                <div className={styles.rightBorder}>
                  <div className={styles.investmentTitle}>
                    Total Amount Invested
                  </div>
                  <div className={styles.investmentSubTitle}>
                    {investmentSummaryStatus === "loading" ? (
                      <ButtonLoader />
                    ) : (
                      formatMoney(
                        investmentSummaryData?.total_amount_invested,
                        getCurrencyInfo(investmentsData?.data[0]?.currency)
                          ?.symbol,
                      ) || "N/A"
                    )}
                  </div>
                </div>
              </div>
              <div className="col-md-4 d-flex flex-column">
                <div className={styles.rightBorder}>
                  <div className={styles.investmentTitle}>
                    Total Interest Payable
                  </div>
                  <div className={styles.investmentSubTitle}>
                    {investmentSummaryStatus === "loading" ? (
                      <ButtonLoader />
                    ) : (
                      formatMoney(
                        investmentSummaryData?.total_interest_payable,
                        getCurrencyInfo(investmentsData?.data[0]?.currency)
                          ?.symbol,
                      ) || "N/A"
                    )}
                  </div>
                </div>
              </div>
            </div>
            <h5 className={`${styles.portfolio} mb-3`}>Investments</h5>

            <div className="row m-x-sm m-y-2 grid__padding">
              <div className="table-responsive">
                <table className="table">
                  <thead className="bg-lighter-grey">
                    <tr>
                      <th>Amount Invested</th>
                      <th>Tenor</th>
                      <th>Total Interest Payable</th>
                      <th>Interest Repayment Type</th>
                      <th>Monthly Yield</th>
                      <th>Reference No</th>
                      <th>Deposit Type</th>
                      <th>Status</th>
                      <th>Start Date</th>
                      <th>MaturityDate</th>
                      <th>Manage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {investmentsData &&
                      investmentsData?.data.length > 0 &&
                      investmentsData?.data.map(
                        (request: IInvestment, _id: number) => {
                          const {
                            principal_amount,
                            tenure,
                            interest_amount,
                            interest_repayment_type,
                            monthly_interest_repayment,
                            reference_number,
                            effective_date,
                            maturity_date,
                            investment_status,
                            funder_id,
                            deposit_type,
                            id: investment_id,
                          } = request;

                          return (
                            <tr key={_id}>
                              <td>
                                {formatMoney(
                                  principal_amount,
                                  getCurrencyInfo(
                                    investmentsData?.data[0]?.currency,
                                  )?.symbol,
                                )}
                              </td>
                              <td>{tenure} days</td>
                              <td>
                                {formatMoney(
                                  interest_amount,
                                  getCurrencyInfo(
                                    investmentsData?.data[0]?.currency,
                                  )?.symbol,
                                )}
                              </td>
                              <td>{interest_repayment_type}</td>
                              <td>
                                {formatMoney(
                                  monthly_interest_repayment,
                                  getCurrencyInfo(
                                    investmentsData?.data[0]?.currency,
                                  )?.symbol,
                                )}
                              </td>
                              <td>{reference_number}</td>
                              <td>{deposit_type}</td>
                              <td>
                                <span
                                  className={attachClassNameToTag(
                                    investment_status,
                                  )}
                                >
                                  {attachClassNameToTag(investment_status)}
                                </span>
                              </td>
                              <td>{effective_date?.substring(0, 10)}</td>
                              <td>{maturity_date?.substring(0, 10)}</td>
                              <td>
                                <div className="dropdown">
                                  <button
                                    className="btn zoom-element btn-slim dropdown-toggle"
                                    type="button"
                                    id="dropdownMenuButton"
                                    data-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                  ></button>
                                  <div
                                    className="dropdown-menu"
                                    aria-labelledby="dropdownMenuButton"
                                  >
                                    {investment_status ===
                                    InvestmentStatus.MATURED ? (
                                      <span
                                        className="cursor-pointer dropdown-item"
                                        id="custom-rollover-button"
                                        onClick={() => {
                                          handleRollOverModalToggle();
                                          setSelectedRecord({
                                            ...request,
                                            investment_product_id: Number(
                                              investment_product_id,
                                            ),
                                          } as ISelectedRecord);
                                        }}
                                      >
                                        ROLL OVER
                                      </span>
                                    ) : null}

                                    <span
                                      className="cursor-pointer dropdown-item"
                                      onClick={() => {
                                        history.push({
                                          pathname: `/investment-details/${funder_id}`,
                                          state: {
                                            data: request,
                                          },
                                        });
                                      }}
                                    >
                                      VIEW DETAILS
                                    </span>
                                    {deposit_type !== "FixedDeposit" &&
                                      roles &&
                                      roles?.length > 0 &&
                                      roles?.includes(
                                        ROLE_TYPES.INVESTMENT_TOP_UP_INITIATOR,
                                      ) &&
                                      investment_status ===
                                        InvestmentStatus.ACTIVE && (
                                        <span
                                          className="cursor-pointer dropdown-item"
                                          id="custom-fund-investment-button"
                                          onClick={() => {
                                            handleFundInvestmentModalToggle();
                                            setSelectedRecord({
                                              ...request,
                                              investment_product_id: Number(
                                                investment_product_id,
                                              ),
                                            } as ISelectedRecord);
                                          }}
                                        >
                                          FUND
                                        </span>
                                      )}

                                    {roles &&
                                      roles?.length > 0 &&
                                      roles?.includes(
                                        ROLE_TYPES.INVESTMENT_WITHDRAWAL_INITIATOR,
                                      ) &&
                                      investment_status ===
                                        InvestmentStatus.ACTIVE && (
                                        <span
                                          className="cursor-pointer dropdown-item"
                                          id="custom-withdraw-investment-button"
                                          onClick={() => {
                                            handleInvestmentWithdrawalModalToggle();
                                            setSelectedRecord({
                                              ...request,
                                              investment_product_id: Number(
                                                investment_product_id,
                                              ),
                                            } as ISelectedRecord);
                                          }}
                                        >
                                          {deposit_type !== "FixedDeposit"
                                            ? "WITHDRAW"
                                            : "PREMATURE LIQUIDATION"}
                                        </span>
                                      )}
                                    {investment_status ===
                                    InvestmentStatus.MATURED ? (
                                      <span
                                        className="cursor-pointer dropdown-item"
                                        onClick={() => {
                                          setIsConfirmationModalOpen(true);
                                          setInvestmentID(investment_id);
                                          setMaturityDate(maturity_date || "");
                                        }}
                                      >
                                        CLOSE
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        },
                      )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      ) : !investmentsData ? (
        <div>
          <div className="grid__padding animated fadeInRight">
            <h4 className="center-txt py-5">
              <i className="fas fa-bell" /> There are currently no Investment
            </h4>
          </div>
        </div>
      ) : (
        <div>
          <div className="grid__padding animated fadeInRight">
            <h4 className="center-txt py-5">
              <i className="fas fa-bell" /> There are currently no Investment
            </h4>
          </div>
        </div>
      )}

      {isConfirmationModalOpen ? (
        <CloseInvestmentModal
          onConfirm={handleInvestmentClosure}
          onClose={() => {
            setIsConfirmationModalOpen(false);
            setInvestmentID(null);
            setMaturityDate("");
          }}
          isOpen={isConfirmationModalOpen}
          isLoading={isLoadingInvestmentClosure}
          maturityDate={maturityDate}
        />
      ) : null}
    </div>
  );
};

export default InvestorInvestment;
